import { cva } from "class-variance-authority";

/** Floating chrome shell — matches showcase/product-mode.css `.toolbar`. */
export const canvasToolbarShellVariants = cva(
  [
    "inline-flex",
    "bg-n-00 border border-n-10 rounded-lg shadow-elev-5",
    "p-1 gap-0.5",
  ],
  {
    variants: {
      orientation: {
        horizontal: "flex-row flex-nowrap items-center",
        vertical: "flex-col items-stretch w-[42px]",
      },
    },
    defaultVariants: {
      orientation: "horizontal",
    },
  },
);

export const canvasToolbarGroupVariants = cva("flex gap-0.5 shrink-0", {
  variants: {
    orientation: {
      horizontal: "flex-row flex-nowrap items-center",
      vertical: "flex-col items-stretch",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

export const canvasToolbarDividerVariants = cva("bg-n-10 shrink-0", {
  variants: {
    orientation: {
      horizontal: "w-px mx-1 self-stretch my-1",
      vertical: "h-px mx-1 my-1 self-auto w-auto",
    },
  },
  defaultVariants: {
    orientation: "horizontal",
  },
});

/** Icon / tool button — matches `.toolbar-btn`. */
export const canvasToolbarButtonVariants = cva(
  [
    "group relative inline-flex items-center justify-center gap-0.5",
    "h-8 min-w-8 px-1.5",
    "rounded-sm border-0 bg-transparent",
    "text-n-90 cursor-pointer outline-none",
    "transition-[background-color,color,opacity] duration-150 ease-out",
    "hover:duration-100",
    "hover:bg-n-10",
    "active:duration-75 active:opacity-92",
    "focus-visible:outline-[1.5px] focus-visible:outline-rust focus-visible:outline-offset-0.5",
    "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
    "disabled:hover:bg-transparent",
    "aria-expanded:bg-n-10",
  ],
  {
    variants: {
      active: {
        true: [
          "bg-[color-mix(in_oklab,var(--rust)_12%,transparent)] text-rust",
          "hover:bg-[color-mix(in_oklab,var(--rust)_18%,transparent)]",
        ],
        false: "",
      },
      destructive: {
        true: "text-lc-blocked hover:bg-lc-blocked/10",
        false: "",
      },
      accent: {
        true: "text-plum hover:bg-plum/10",
        false: "",
      },
      hasDropdown: {
        true: "pr-1.5",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
      destructive: false,
      accent: false,
      hasDropdown: false,
    },
  },
);

export const canvasToolbarDisplayVariants = cva(
  "inline-flex items-center justify-center h-7 min-w-8 px-0.5 font-mono text-xs leading-none text-n-70",
);

export const canvasToolbarButtonTextVariants = cva(
  "inline-flex items-center px-1 font-sans text-sm leading-none text-n-90 whitespace-nowrap",
  {
    variants: {
      mono: {
        true: "font-mono text-xs",
        false: "",
      },
    },
    defaultVariants: {
      mono: false,
    },
  },
);

/** Matches showcase `.toolbar-color-swatch` — 14px circle, stroke ring. */
export const canvasToolbarColorSwatchClassName =
  "size-3.5 shrink-0 rounded-full border border-n-90";

/** Matches showcase `.toolbar-color-swatch--none`. */
export const canvasToolbarColorSwatchNoneClassName =
  "size-3.5 shrink-0 rounded-full border border-n-30 bg-n-00 bg-[linear-gradient(135deg,transparent_calc(50%-1px),color-mix(in_oklab,var(--rust)_75%,transparent)_calc(50%-1px),color-mix(in_oklab,var(--rust)_75%,transparent)_calc(50%+1px),transparent_calc(50%+1px))]";

/** List-style submenu panel — matches Type-1 toolbar shell chrome. */
export const canvasToolbarSubmenuPanelClassName =
  "min-w-40 rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5";

/** Radix dropdown panel on a canvas toolbar — same chrome as `.toolbar` shell. */
export const canvasToolbarDropdownContentClassName =
  "rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5 text-n-90";

/** Row inside a list-style toolbar submenu. */
export const canvasToolbarSubmenuRowClassName =
  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-n-10";

export const canvasToolbarSubmenuRowActiveClassName =
  "bg-[color-mix(in_oklab,var(--rust)_12%,transparent)] text-rust font-medium";

/** Plum accent for entity/process toolbar actions (outside the canvas-tool spec). */
export const canvasToolbarAccentButtonVariants = cva([
  "relative inline-flex items-center justify-center",
  "h-8 min-w-8 px-1.5",
  "rounded-sm border-0 bg-transparent",
  "text-plum cursor-pointer outline-none",
  "transition-[background-color,color,opacity] duration-150 ease-out",
  "hover:duration-100 hover:bg-plum/10",
  "active:duration-75 active:opacity-92",
  "focus-visible:outline-[1.5px] focus-visible:outline-rust focus-visible:outline-offset-0.5",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
]);
